import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { Observable ,  Subscription, Subject, BehaviorSubject } from 'rxjs';
import { filter, tap } from "rxjs/operators";

import { OrganizationStub, Organization, MassMailingEntry, OrganizationComplianceDetails, mergeFieldsOrgInfo, FinancialComplianceSearchSpec } from '../models/organization.model';
import { DocumentWithLink } from "../models/document-tag.model";
import { LunaDomain } from '../../builder/models/domain.model';
import { DomainDefinitionService } from '../../builder/services/domain-definition.service';
import { ApiService, ParamList } from './api.service';

@Injectable()
export class OrganizationService {
    domain: LunaDomain;
	organizations: Organization[] = [];
	subscriptions: Subscription = new Subscription();

	private _entityTypeUpdate: Subject<string> = new Subject<string>();
	public entityUpdateObservable = this._entityTypeUpdate.asObservable();


    constructor(
        public http: HttpClient,
        public domainService: DomainDefinitionService,
        public apiService: ApiService,
        private router: Router
    ) {
        this.subscriptions.add(this.domainService.get('BUSINESS_TYPE').subscribe(domain => {
            this.domain = domain;
        }, (error) => {
            console.error(error);
        }));

        /*
        this.subscriptions.add(this.getList().subscribe(orgs => {
            this.organizations = orgs;
        }, (error) => {
            console.log(error);
        }));
        */

        if (this.router.events) {
            this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
                this.subscriptions.unsubscribe();
            });
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    checkOrganizationCompliance(orgIds: string[] = null): Observable<OrganizationComplianceDetails[]> {

        let options = {
            params: {
                orgIds: orgIds
            }
        };

        return this.http.get<OrganizationComplianceDetails[]>('/api/organization/compliance', options);
    }

    filterOrganizationName(id: string): string {
        if (!this.organizations) return '';
        const org = this.organizations.filter(v => v.id === id);
        return org[0] ? org[0].name : '';
    }

    filterTypeDescription(code: string): string {
        if (!this.domain) return '';
        const domainValue = this.domain.values.filter(v => v.Code === code);
        return domainValue[0] ? domainValue[0].Definition : '';
    }

    getAll(): Observable<Organization[]> {
        return this.http.get<Organization[]>(`/api/organization`);
    }

    getDataForNameValidation(): Observable<OrganizationStub[]> { //update this endpoint to use the right return type
        return this.http.get<OrganizationStub[]>("/api/organization/get-data-for-name-validation");
    }

    getList(): Observable<Organization[]> {
        return this.http.get<Organization[]>(`/api/organization/list`);
    }

    getInternalList(): Observable<Organization[]> {
		return this.http.get<Organization[]>(`/api/organization/list-internal`).pipe(
			tap((data: Organization[]) => {
				this.updateOrganizationList(data);
			})
		);
    }

    getExternalList(): Observable<Organization[]> {
		return this.http.get<Organization[]>(`/api/organization/list-external`).pipe(
			tap((data: Organization[]) => {
				this.updateOrganizationList(data);
			})
		);
    }

    getAllExternalOrganizations(): Observable<Organization[]> {
		return this.http.get<Organization[]>(`/api/organization/list-external-all`).pipe(
			tap((data: Organization[]) => {
				this.updateOrganizationList(data);
			})
		);
    }

    getMassMailingList(): Observable<MassMailingEntry[]> {
        return this.http.get<MassMailingEntry[]>(`/api/organization/mass-mailing`);
    }

    getLegacyOrgid(orgId: string): Observable<string> {
        return this.http.get<string>(`/api/organization/legacyOrgId/${orgId}`);
    }

    getRicsOrgid(legacyOrgId: string): Observable<string> {
        return this.http.get<string>(`/api/organization/ricsOrgId/${legacyOrgId}`);
    }

    getOrganization(orgId: string): Observable<Organization> {
		return this.http.get<Organization>(`/api/organization/${orgId}`).pipe(
			tap((data: Organization) => {
				this.updateOrganizationList([data]);
			})
		);
    }
    
    getOrganizationStub(orgId: string): Observable<OrganizationStub> {
		return this.http.get<OrganizationStub>(`/api/organization/stub/${orgId}`).pipe(
			tap((data: Organization) => {
				this.updateOrganizationList([data]);
			})
		);;
    }
    getOrgAndMergeFieldInfo(orgId: string): Observable<mergeFieldsOrgInfo> {
        return this.http.get<mergeFieldsOrgInfo>(`/api/organization/organdmergefields/${orgId}/`);
    }

    getOrganizationStubByHistoricalOwnerNumber(ownerNumber: string): Observable<OrganizationStub> {
		return this.http.get<OrganizationStub>(`/api/organization/get-stub-by-historic-number/${ownerNumber}`).pipe(
			tap((data: Organization) => {
				this.updateOrganizationList([data]);
			})
		);
    }

    getOrganizationStubsByPartialName(partialOrgName: string): Observable<OrganizationStub[]> {
		return this.http.get<OrganizationStub[]>(`/api/organization/get-by-partial-name/${partialOrgName}`).pipe(
			tap((data: Organization[]) => {
				this.updateOrganizationList(data);
			})
		);
    }

    getOrganizationStubs(orgIds: string[]): Observable<OrganizationStub[]> {
        let options = {
            params: {
                orgIds: orgIds
            }
        };

        return this.http.get<OrganizationStub[]>('/api/organization/stubs', options).pipe(
			tap((data: Organization[]) => {
				this.updateOrganizationList(data);
			})
		);
    }

    getOrganizationFiles(orgId: string, orgIds: Array<string>): Observable<DocumentWithLink[]> {
        return this.http.get<DocumentWithLink[]>(`/api/organization/${orgId}/files` + this.apiService.buildParameterList([new ParamList('historicIds', orgIds)]));
    }
    getOrganizationName(id: string): string {
        return this.filterOrganizationName(id);
    }

    getTypeDescription(code: string): string {
        return this.filterTypeDescription(code);
    }

    save(org: Organization): Observable<Organization> {
		return this.http.post<Organization>(`/api/organization/Save`, org).pipe(
			tap((data: Organization) => {
				this.updateOrganizationList([data]);
			})
		);
    }

    setIsActive(orgId: string, active: boolean): Observable<Organization> {
        return this.http.put<Organization>
			(`/api/organization/active-status/${orgId}/${active}`, undefined).pipe(
				tap((data: Organization) => {
					this.updateOrganizationList([data]);
				})
			);
    }

    updateCompliance(orgId: string): Observable<OrganizationComplianceDetails> {
        return this.http.get<OrganizationComplianceDetails>(`/api/organization/update-compliance-manual/${orgId}`);
    }

    getComplianceData(spec: FinancialComplianceSearchSpec): Observable<OrganizationComplianceDetails[]> {
        return this.http.post<OrganizationComplianceDetails[]>(`/api/organization/query-compliance`, spec);
	}

	updateEntityType(update: string): void {
		this._entityTypeUpdate.next(update);
	}

	private updateOrganizationList(updates: Organization[]): void {
		if (this.organizations == null || this.organizations.length == 0) {
			this.organizations = updates;
		} else {
			updates.forEach(org => {
				if (org != null && org.id != null && org.id.length > 0) {
					let index = this.organizations.findIndex(o => o.id == org.id);
					if (index == -1) {
						this.organizations.push(org);
					} else {
						this.organizations.splice(index, 1, org);
					}
				}
			})
		}
	}
}
